// GarsonCagirmaSekmesi.js
// Konum: src/GarsonCagirmaSekmesi.js — CagriBellAdmin.js içinde kullanılıyor

import { useEffect, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { authFetch } from './authFetch';

const BACKEND = process.env.REACT_APP_API_URL;

export default function GarsonCagirmaSekmesi({ restaurantId }) {
  const [bolum, setBolum] = useState('masalar');
  const [masalar, setMasalar] = useState([]);
  const [garsonlar, setGarsonlar] = useState([]);
  const [istatistikler, setIstatistikler] = useState([]);
  const [yeniMasa, setYeniMasa] = useState('');
  const [yeniGarson, setYeniGarson] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [acikGarson, setAcikGarson] = useState(null);

  const masalariGetir = async () => {
    const res = await authFetch(`${BACKEND}/call-waiter/tables/${restaurantId}`);
    setMasalar(await res.json());
  };

  const garsonlariGetir = async () => {
    const res = await authFetch(`${BACKEND}/call-waiter/waiters/${restaurantId}`);
    setGarsonlar(await res.json());
  };

  const istatistikleriGetir = async () => {
    const res = await authFetch(`${BACKEND}/call-waiter/stats/${restaurantId}`);
    setIstatistikler(await res.json());
  };

  useEffect(() => {
    masalariGetir();
    garsonlariGetir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  useEffect(() => {
    if (bolum === 'performans') istatistikleriGetir();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bolum, restaurantId]);

  const masaEkle = async () => {
    if (!yeniMasa.trim()) return;
    setYukleniyor(true);
    await authFetch(`${BACKEND}/call-waiter/tables`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurantId, tableNumber: yeniMasa.trim() }),
    });
    setYeniMasa('');
    await masalariGetir();
    setYukleniyor(false);
  };

  const masaSil = async (tableId, tableNumber) => {
    if (!window.confirm(`Masa ${tableNumber} silinsin mi? Bu masanın QR kodu artık çalışmaz.`)) return;
    await authFetch(`${BACKEND}/call-waiter/tables/${restaurantId}/${tableId}`, { method: 'DELETE' });
    await masalariGetir();
  };

  const garsonEkle = async () => {
    if (!yeniGarson.trim()) return;
    setYukleniyor(true);
    await authFetch(`${BACKEND}/call-waiter/waiters`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ restaurantId, name: yeniGarson.trim() }),
    });
    setYeniGarson('');
    await garsonlariGetir();
    setYukleniyor(false);
  };

  const garsonPasifle = async (waiterId) => {
    await authFetch(`${BACKEND}/call-waiter/waiters/${restaurantId}/${waiterId}/deactivate`, { method: 'POST' });
    await garsonlariGetir();
  };

  const garsonAktifle = async (waiterId) => {
    await authFetch(`${BACKEND}/call-waiter/waiters/${restaurantId}/${waiterId}/activate`, { method: 'POST' });
    await garsonlariGetir();
  };

  const masaAdiniDuzenle = async (tableId, mevcutAd) => {
    const yeniAd = window.prompt('Yeni masa adı:', mevcutAd);
    if (!yeniAd || !yeniAd.trim() || yeniAd.trim() === mevcutAd) return;
    await authFetch(`${BACKEND}/call-waiter/tables/${restaurantId}/${tableId}/rename`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tableNumber: yeniAd.trim() }),
    });
    await masalariGetir();
  };

  const qrIndir = (tableNumber, canvasId) => {
    const canvas = document.getElementById(canvasId);
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `masa-${tableNumber}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className="py-5">
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setBolum('masalar')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            bolum === 'masalar' ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-400'
          }`}
        >
          Masalar / QR
        </button>
        <button
          onClick={() => setBolum('garsonlar')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            bolum === 'garsonlar' ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-400'
          }`}
        >
          Garsonlar
        </button>
        <button
          onClick={() => setBolum('performans')}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${
            bolum === 'performans' ? 'border-orange-500 text-orange-500' : 'border-transparent text-gray-400'
          }`}
        >
          Performans
        </button>
      </div>

      {bolum === 'masalar' && (
        <div>
          <div className="flex gap-2.5 mb-6">
            <input
              value={yeniMasa}
              onChange={(e) => setYeniMasa(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && masaEkle()}
              placeholder="Masa adı (örn: 4 veya Bahçe 2)"
              className="flex-1 max-w-xs border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-orange-400"
            />
            <button
              onClick={masaEkle}
              disabled={yukleniyor}
              className="bg-orange-500 text-white rounded-lg px-4.5 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              + Masa Ekle
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {masalar.map((masa) => {
              const canvasId = `qr-${masa.id}`;
              const url = `${window.location.origin}/cagir/${restaurantId}/${masa.id}/${masa.tableNumber}`;
              return (
                <div key={masa.id} className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col items-center gap-3 shadow-sm">
                  <div className="font-bold text-gray-800">Masa {masa.tableNumber}</div>
                  <QRCodeCanvas id={canvasId} value={url} size={140} />
                  <div className="flex gap-2 flex-wrap justify-center">
                    <button
                      onClick={() => masaAdiniDuzenle(masa.id, masa.tableNumber)}
                      className="bg-gray-100 text-gray-700 rounded-md px-3 py-1.5 text-xs font-medium"
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(url);
                        alert('Link kopyalandı: ' + url);
                      }}
                      className="bg-blue-50 text-blue-600 rounded-md px-3 py-1.5 text-xs font-medium"
                    >
                      Linki Kopyala
                    </button>
                    <button
                      onClick={() => qrIndir(masa.tableNumber, canvasId)}
                      className="bg-gray-100 text-gray-700 rounded-md px-3 py-1.5 text-xs font-medium"
                    >
                      İndir
                    </button>
                    <button
                      onClick={() => masaSil(masa.id, masa.tableNumber)}
                      className="bg-red-50 text-red-500 rounded-md px-3 py-1.5 text-xs font-medium"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {bolum === 'garsonlar' && (
        <div>
          <div className="flex gap-2.5 mb-6">
            <input
              value={yeniGarson}
              onChange={(e) => setYeniGarson(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && garsonEkle()}
              placeholder="Garson adı"
              className="flex-1 max-w-xs border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-orange-400"
            />
            <button
              onClick={garsonEkle}
              disabled={yukleniyor}
              className="bg-orange-500 text-white rounded-lg px-4.5 py-2.5 text-sm font-semibold disabled:opacity-60"
            >
              + Garson Ekle
            </button>
          </div>

          <div className="flex flex-col gap-2 max-w-md">
            {garsonlar.map((garson) => (
              <div key={garson.id} className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3">
                <span className={`w-2 h-2 rounded-full ${garson.active ? 'bg-green-400' : 'bg-gray-300'}`} />
                <span className="flex-1 font-semibold text-gray-800">{garson.name}</span>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/garson/${restaurantId}/${garson.id}`;
                    navigator.clipboard.writeText(url);
                    alert('Garson linki kopyalandı: ' + url);
                  }}
                  className="bg-blue-50 text-blue-600 rounded-md px-3 py-1.5 text-xs font-medium"
                >
                  Linki Kopyala
                </button>
                {garson.active ? (
                  <button
                    onClick={() => garsonPasifle(garson.id)}
                    className="border border-gray-200 text-gray-500 rounded-md px-3 py-1.5 text-xs"
                  >
                    Pasif Yap
                  </button>
                ) : (
                  <button
                    onClick={() => garsonAktifle(garson.id)}
                    className="bg-green-50 text-green-600 rounded-md px-3 py-1.5 text-xs font-medium"
                  >
                    Aktif Yap
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {bolum === 'performans' && (
        <div>
          {istatistikler.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">
              Henüz onaylanmış çağrı yok — garsonlar çağrıları karşıladıkça burada birikecek.
            </div>
          ) : (
            <div className="flex flex-col gap-3 max-w-2xl">
              {istatistikler.map((stat) => (
                <div key={stat.waiterId} className="border border-gray-200 rounded-2xl overflow-hidden">
                  <button
                    onClick={() => setAcikGarson(acikGarson === stat.waiterId ? null : stat.waiterId)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-white"
                  >
                    <span className="font-bold text-gray-800">{stat.waiterName}</span>
                    <div className="flex items-center gap-3">
                      <span className="bg-orange-50 text-orange-600 rounded-full px-3 py-1 text-xs font-bold">
                        {stat.totalCalls} çağrı
                      </span>
                      <span className="text-gray-400 text-xs">
                        {acikGarson === stat.waiterId ? '▲' : '▼'}
                      </span>
                    </div>
                  </button>
                  {acikGarson === stat.waiterId && (
                    <div className="bg-gray-50 px-5 py-4 border-t border-gray-200">
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-3">
                        Masa Bazlı Dağılım
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {stat.tables.map((t) => (
                          <div key={t.tableNumber} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">Masa {t.tableNumber}</span>
                            <span className="text-gray-500 font-medium">{t.count} kez</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}